import { ChevronLeft, ChevronRight } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { useOnline } from "@/hooks/use-online"
import { cn } from "@/lib/utils"
import type { CrewPagination } from "@/types/crew"

/**
 * OKS-25 F9 — Crew Roster pagination footer.
 *
 * Mirrors OKS-17's `<CandidatePaginationFooter>` semantics 1:1: prev /
 * "Page X of Y" / next; disabled when offline; "Showing X–Y of Z"
 * summary on the leading side. 25/page locked at the page wire-up
 * level (per BE TRD §Pagination).
 *
 * Locked testid contract:
 *   - `crew-list-pagination`        — footer wrapper
 *   - `crew-list-pagination-prev`   — prev button
 *   - `crew-list-pagination-next`   — next button
 */
interface CrewListPaginationFooterProps {
  pagination: CrewPagination
  perPage?: number
  onPageChange: (page: number) => void
  className?: string
}

export function CrewListPaginationFooter({
  pagination,
  perPage = 25,
  onPageChange,
  className,
}: CrewListPaginationFooterProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const { page, pages, count } = pagination

  if (count === 0) {
    return (
      <div
        data-testid="crew-list-pagination"
        className={cn(
          "text-muted-foreground flex justify-center py-4 text-sm",
          className,
        )}
      >
        {t("crew.list.pagination.summary", { from: 0, to: 0, count: 0 })}
      </div>
    )
  }

  const from = (page - 1) * perPage + 1
  const to = Math.min(page * perPage, count)
  const isFirst = page <= 1
  const isLast = page >= pages
  const disabledPrev = isFirst || !online
  const disabledNext = isLast || !online

  return (
    <nav
      data-testid="crew-list-pagination"
      aria-label={t("crew.list.pagination.summary", { from, to, count })}
      className={cn(
        "flex flex-col items-center gap-3 py-4 sm:flex-row sm:justify-between",
        className,
      )}
    >
      <p className="text-muted-foreground text-sm tabular-nums">
        {t("crew.list.pagination.summary", { from, to, count })}
      </p>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabledPrev}
          aria-disabled={disabledPrev}
          data-testid="crew-list-pagination-prev"
          onClick={() => onPageChange(page - 1)}
        >
          <ChevronLeft className="size-4" aria-hidden="true" />
        </Button>
        <span className="text-muted-foreground text-sm tabular-nums">
          {page} / {pages}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabledNext}
          aria-disabled={disabledNext}
          data-testid="crew-list-pagination-next"
          onClick={() => onPageChange(page + 1)}
        >
          <ChevronRight className="size-4" aria-hidden="true" />
        </Button>
      </div>
    </nav>
  )
}
